<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { AppDropdown, AppDropdownItem } from '@/components/molecules/AppDropdown'
import { useConfigStore } from '@/stores/ConfigStore'
import { useBillingStore } from '@/stores/BillingStore'
import { useNewDesign } from '@/hooks/useNewDesign'
import { useInventoryPilot } from '@/helpers/featureFlags'

const props = defineProps({
  user: Object,
})

const emit = defineEmits(['closeSidebar', 'upgrade', 'signOut'])

const router = useRouter()
const configStore = useConfigStore()
const billingStore = useBillingStore()
const { isNewDesign, setNewDesign } = useNewDesign()
const isInventoryPilot = useInventoryPilot()

const subscription = computed(() => configStore.getConfigField('subscription'))
const hasActiveSubscription = computed(() => configStore.getConfigField('hasActiveSubscription'))
const topPlan = computed(() => billingStore.topPlan)

const canUpgrade = computed(() => hasActiveSubscription.value && subscription.value?.plan_id !== topPlan.value?.id)
const canReactivate = computed(() => subscription.value && !hasActiveSubscription.value)

const switchLabel = computed(() => (isNewDesign.value ? 'Switch to old view' : 'Switch to new view'))

const go = (to) => {
  emit('closeSidebar')
  router.push(to)
}

const openDesktopApp = () => {
  emit('closeSidebar')
  window.open('https://www.carddealerpro.com/trading-card-scanner', '_blank')
}

const toggleDesign = () => {
  emit('closeSidebar')
  setNewDesign(!isNewDesign.value)
}

const signOut = () => {
  emit('signOut')
  router.push('/sign-in')
}
</script>

<template>
  <AppDropdown placement="bottom-end" width="w-60 max-md:w-[calc(100vw-2rem)]">
    <template #trigger="{ open }">
      <button type="button" data-tour="user-menu" class="flex items-center gap-x-0.5" @click="$emit('closeSidebar')">
        <img
          v-if="user?.avatar_image"
          :src="user.avatar_image"
          class="w-7 h-7 object-cover rounded-full ring-1 ring-blue-600 shrink-0"
        />
        <span
          v-else
          class="w-7 h-7 object-cover rounded-full ring-1 ring-blue-600 flex items-center justify-center text-tint-1 bg-blue-50"
          v-text="user?.first_name?.slice(0, 1).toUpperCase()"
        />
        <Icon
          icon="heroicons:chevron-down-16-solid"
          class="text-base text-white transition-all duration-300"
          :class="{ 'rotate-180': open }"
        />
      </button>
    </template>

    <!-- Header: name + email -->
    <div class="px-4 py-3 border-b border-gray-100">
      <h4 class="font-semibold text-gray-900 truncate" v-text="`${user?.first_name} ${user?.last_name}`" />
      <span class="block text-sm text-gray-500 truncate" v-text="user?.email" />
    </div>

    <!-- The only way back once you're in the redesign — so it follows the
         same rollout flag rather than being always-on. -->
    <AppDropdownItem v-if="isInventoryPilot" @select="toggleDesign">{{ switchLabel }}</AppDropdownItem>
    <AppDropdownItem @select="go('/settings/account')">Account settings</AppDropdownItem>
    <AppDropdownItem @select="go('/settings/plans')">Billing</AppDropdownItem>
    <AppDropdownItem @select="openDesktopApp">Get the Desktop App</AppDropdownItem>

    <AppDropdownItem v-if="canUpgrade" icon="heroicons:fire-16-solid" icon-class="size-4 text-tint-1" @select="[$emit('closeSidebar'), $emit('upgrade')]">
      Upgrade
    </AppDropdownItem>
    <AppDropdownItem v-else-if="canReactivate" icon="heroicons:bolt-16-solid" icon-class="size-4 text-tint-1" @select="[$emit('closeSidebar'), $emit('upgrade')]">
      Reactivate Account
    </AppDropdownItem>

    <AppDropdownItem divider danger @select="signOut">Log out</AppDropdownItem>
  </AppDropdown>
</template>
